import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, QrCode, Camera, CameraOff, PawPrint } from 'lucide-react';
import { animalsApi } from '../services/resources';
import { getApiErrorMessage } from '../services/api';
import { ILMSPageHeader } from '../components/ILMSPageHeader';
import { ILMSCard } from '../components/ILMSCard';
import { ILMSButton } from '../components/ILMSButton';
import { ILMSAlert } from '../components/ILMSAlert';
import { ILMSInput } from '../components/ILMSForm';

export default function QRScanPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scannerActive, setScannerActive] = useState(false);
  const scannerRef = useRef<import('html5-qrcode').Html5Qrcode | null>(null);
  const scannerElId = 'ilms-qr-scanner';

  const lookup = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError('Enter a QR value or animal ID to search.');
      return;
    }
    setSearching(true);
    setError(null);
    try {
      // WF-02: the QR-scan convenience endpoint resolves a scanned QR straight
      // to the Animal profile — no manual Asset lookup step needed.
      const animal = await animalsApi.byQr(trimmed);
      navigate(`/animals/${animal.id}`);
    } catch {
      try {
        const animal = await animalsApi.findByCode(trimmed);
        navigate(`/animals/${animal.id}`);
      } catch (err2) {
        setError(getApiErrorMessage(err2, `No animal found matching "${trimmed}". Please check the QR value or animal ID and try again.`));
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    lookup(query);
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch {
        // scanner already stopped
      }
      scannerRef.current = null;
    }
    setScannerActive(false);
  };

  const startScanner = async () => {
    setError(null);
    setScannerActive(true); // mounts/reveals the target <div> before we touch html5-qrcode

    const config = { fps: 10, qrbox: { width: 240, height: 240 } };
    const onDecoded = (decodedText: string) => {
      stopScanner();
      lookup(decodedText);
    };
    const onFrameFailure = () => {
      // per-frame decode failure — ignore, this fires continuously while scanning
    };

    // Everything below — including the constructor, which throws
    // synchronously if its target element isn't in the DOM yet — is now
    // inside this try/catch, so nothing can fail as a silent, invisible,
    // unhandled promise rejection again.
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const instance = new Html5Qrcode(scannerElId);
      scannerRef.current = instance;
      try {
        // Prefer the rear camera (best for scanning a physical tag on a phone).
        await instance.start({ facingMode: 'environment' }, config, onDecoded, onFrameFailure);
      } catch (firstErr) {
        // Laptops/desktops usually only have a front-facing webcam, which can
        // make a strict "environment" request fail even though a usable camera
        // exists — retry with no facing-mode constraint (any available camera)
        // before giving up.
        await instance.start({ facingMode: 'user' }, config, onDecoded, onFrameFailure).catch(() => {
          throw firstErr; // surface the original (usually more informative) error
        });
      }
    } catch (err) {
      setScannerActive(false);
      const name = err instanceof Error ? err.name : undefined;
      const messages: Record<string, string> = {
        NotAllowedError: 'Camera permission was denied. Allow camera access for this site in your browser settings, then try again.',
        NotFoundError: 'No camera was found on this device. Use the search box below instead.',
        NotReadableError: 'The camera could not be started — it may be in use by another app (e.g. Zoom, Teams). Close other apps using the camera and try again.',
        OverconstrainedError: 'No camera on this device matches the requested settings. Use the search box below instead.',
      };
      setError(
        (name && messages[name]) ||
          `Unable to access the camera${err instanceof Error && err.message ? ` (${err.message})` : `: ${String(err)}`}. Please allow camera permission or use the search box below.`
      );
    }
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <ILMSPageHeader title="Scan QR / Search Animal" subtitle="Scan an animal's QR tag, or search by animal ID or QR value." />

      {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}

      {/* Mobile-first: prominent camera scan */}
      <ILMSCard title="Scan with Camera">
        {/* Both branches stay mounted (toggled with `hidden`, not conditional
            rendering): html5-qrcode needs its target <div id={scannerElId}>
            to already exist in the DOM at the moment `new Html5Qrcode(...)`
            runs. Since React state updates are async, gating this div behind
            `{scannerActive && ...}` meant the div didn't exist yet when the
            instance was constructed — the library silently had nowhere to
            attach the video, with no error at all. */}
        <div className="flex flex-col items-center text-center py-6" hidden={scannerActive}>
          <div className="h-16 w-16 rounded-full bg-brand-gold-50 text-brand-gold-600 flex items-center justify-center mb-3">
            <QrCode className="h-8 w-8" />
          </div>
          <p className="text-secondary text-ink-500 mb-4 max-w-xs">
            Point your camera at the animal's QR tag to open its profile instantly.
          </p>
          <ILMSButton size="lg" icon={<Camera />} onClick={startScanner}>
            Start Camera
          </ILMSButton>
        </div>
        <div className="flex flex-col items-center gap-3" hidden={!scannerActive}>
          <div id={scannerElId} className="w-full max-w-sm rounded-md overflow-hidden bg-black min-h-[240px]" />
          <ILMSButton variant="outline" icon={<CameraOff />} onClick={stopScanner}>
            Stop Scanning
          </ILMSButton>
        </div>
      </ILMSCard>

      {/* Desktop-first: search box */}
      <ILMSCard title="Search by Animal ID / QR">
        <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-2.5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <ILMSInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter animal ID or QR value…"
              className="pl-9"
              autoFocus
            />
          </div>
          <ILMSButton type="submit" loading={searching} icon={<PawPrint />}>
            Find Animal
          </ILMSButton>
        </form>
      </ILMSCard>
    </div>
  );
}
